import {
  useEffect, useId, useRef, useState, type ReactNode,
} from 'react';
import './rahmen.css';

/**
 * Ein Aufklappmenü mit dem Verhalten, das der Auftrag in den Abschnitten 28
 * und 29 verlangt - einmal gebaut, von Benutzer- und Hilfemenü und vom
 * Projektumschalter gemeinsam benutzt (Abschnitt 44: keine Dubletten).
 *
 * Verhalten:
 * - Klick auf den Auslöser öffnet und schließt.
 * - ESC schließt und gibt den Tastaturfokus an den Auslöser zurück
 *   (Abschnitt 29: "ESC Dialog/Drawer schließen").
 * - Klick außerhalb schließt.
 * - `aria-haspopup`, `aria-expanded` und die Verknüpfung über `aria-controls`
 *   sind gesetzt; die Fläche selbst ist ein `role="menu"` mit
 *   `role="menuitem"`-Kindern (Abschnitt 28).
 * - Pfeil runter/hoch wandert durch die Einträge, Pos1/Ende springt.
 *
 * Bewusst KEIN Portal: Die Fläche liegt im Kopf und wird über
 * `--ebene-menue` nach vorn gebracht. Ein Portal bräuchte eine eigene
 * Positionsrechnung und wäre bei einem Element, das immer am selben Platz
 * hängt, nur eine zusätzliche Fehlerquelle.
 */
export interface AufklappmenueProps {
  /** Inhalt des Auslösers (Symbol, Kürzel, Text). */
  ausloeser: ReactNode;
  /** Beschriftung des Auslösers für Screenreader und Tooltip. */
  ausloeserBeschriftung: string;
  /** Zusätzliche Klasse für den Auslöser. */
  ausloeserKlasse?: string;
  /** Ausrichtung der Fläche unter dem Auslöser. */
  ausrichtung?: 'links' | 'rechts';
  children: ReactNode;
}

export function Aufklappmenue({
  ausloeser, ausloeserBeschriftung, ausloeserKlasse = '',
  ausrichtung = 'rechts', children,
}: AufklappmenueProps) {
  const [offen, setOffen] = useState(false);
  const huelle = useRef<HTMLDivElement>(null);
  const knopf = useRef<HTMLButtonElement>(null);
  const flaeche = useRef<HTMLDivElement>(null);
  const id = useId();

  // Schließen bei Klick außerhalb und bei ESC. Beide Hörer hängen am
  // Dokument und nur solange das Menü offen ist - ein dauerhaft
  // registrierter Hörer je Menü wäre bei drei Menüs im Kopf drei Hörer,
  // die bei jedem Klick der Anwendung mitlaufen.
  useEffect(() => {
    if (!offen) return undefined;

    function beiKlick(ereignis: MouseEvent) {
      if (!huelle.current?.contains(ereignis.target as Node)) setOffen(false);
    }

    function beiTaste(ereignis: KeyboardEvent) {
      if (ereignis.key === 'Escape') {
        setOffen(false);
        knopf.current?.focus();
        return;
      }
      if (ereignis.key !== 'ArrowDown' && ereignis.key !== 'ArrowUp'
          && ereignis.key !== 'Home' && ereignis.key !== 'End') return;

      const punkte = Array.from(
        flaeche.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [],
      );
      if (punkte.length === 0) return;
      ereignis.preventDefault();

      const jetzt = punkte.indexOf(document.activeElement as HTMLElement);
      const ziel = ereignis.key === 'Home' ? 0
        : ereignis.key === 'End' ? punkte.length - 1
          : ereignis.key === 'ArrowDown' ? (jetzt + 1) % punkte.length
            : (jetzt - 1 + punkte.length) % punkte.length;
      punkte[ziel].focus();
    }

    document.addEventListener('mousedown', beiKlick);
    document.addEventListener('keydown', beiTaste);
    return () => {
      document.removeEventListener('mousedown', beiKlick);
      document.removeEventListener('keydown', beiTaste);
    };
  }, [offen]);

  return (
    <div className="aufklappmenue" ref={huelle}>
      <button
        type="button"
        ref={knopf}
        className={`aufklappmenue-ausloeser ${ausloeserKlasse}`.trim()}
        aria-haspopup="menu"
        aria-expanded={offen}
        aria-controls={offen ? id : undefined}
        aria-label={ausloeserBeschriftung}
        title={ausloeserBeschriftung}
        onClick={() => setOffen((vorher) => !vorher)}
      >
        {ausloeser}
      </button>

      {offen && (
        <div
          className={`aufklappmenue-flaeche ${ausrichtung}`}
          id={id}
          role="menu"
          ref={flaeche}
          aria-label={ausloeserBeschriftung}
          onClick={() => setOffen(false)}
        >
          {children}
        </div>
      )}
    </div>
  );
}
